/**
 * Escala fixa dos mapas (Saffron e rotas).
 *
 * O canvas do Pixi e o mundo em DOM eram dimensionados em pixels de CSS
 * (`host.clientWidth/Height`), então qualquer coisa que aumentasse a contagem
 * de px de CSS na mesma área física — zoom out do navegador, janela mais alta —
 * revelava mais mundo e desenhava o mapa pequeno. Ler o nível de zoom não
 * resolve: `devicePixelRatio` mistura zoom com a escala do SO e recarregar a
 * página já zoomado derrota qualquer baseline guardado no load.
 *
 * Então a escala não depende do zoom. A cena mostra sempre `MAP_REF_HEIGHT` px
 * de mundo na vertical e o mapa inteiro (canvas + overlays em DOM) é escalado
 * por `--map-scale` para preencher a caixa. Zoom e resize passam a mudar só o
 * tamanho aparente do mapa, nunca quanto mundo cabe nele.
 *
 * A UI continua zoomando normalmente: nada fora da cena do mapa lê a variável.
 */

/** Altura visível de mundo, em px de mundo (CELL = 80 → ~11,75 tiles). */
export const MAP_REF_HEIGHT = 940;

/** `?mapref=N` ajusta o enquadramento sem recompilar, para calibrar. */
export function mapRefHeight() {
  const raw = Number(new URLSearchParams(location.search).get('mapref'));
  return Number.isFinite(raw) && raw > 0 ? raw : MAP_REF_HEIGHT;
}

/** Pure helper used by the browser code and the zoom regression test. */
export function mapScaleForHeight(height, ref = MAP_REF_HEIGHT) {
  const visibleHeight = Number(height);
  const reference = Number(ref);
  if (!Number.isFinite(visibleHeight) || visibleHeight <= 0 || !Number.isFinite(reference) || reference <= 0) return 1;
  return visibleHeight / reference;
}

/**
 * Mantém `--map-scale` na cena igual a `alturaDaCena / MAP_REF_HEIGHT`.
 * Trava pelo eixo vertical: a largura visível continua acompanhando o aspect
 * ratio da janela, que é o que evita barras vazias em telas widescreen.
 */
export function lockMapScale(scene) {
  if (!scene) return () => {};
  if (scene.__idlemonMapScaleCleanup) return scene.__idlemonMapScaleCleanup;
  scene.dataset.mapScaleLocked = '1';
  const ref = mapRefHeight();

  const apply = () => {
    const rectHeight = typeof scene.getBoundingClientRect === 'function'
      ? scene.getBoundingClientRect().height
      : 0;
    const height = scene.clientHeight || rectHeight || 0;
    if (!height) return;
    scene.style.setProperty('--map-scale', String(mapScaleForHeight(height, ref)));
  };

  apply();
  // ResizeObserver covers layout/view changes. `resize` and visualViewport are
  // both needed because browser zoom and OS display scaling do not emit exactly
  // the same event in Chromium. pageshow handles BFCache return before the next
  // renderer measurement.
  const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(apply) : null;
  observer?.observe(scene);
  window.addEventListener('resize', apply);
  window.visualViewport?.addEventListener('resize', apply, { passive: true });
  window.addEventListener('pageshow', apply);

  const cleanup = () => {
    observer?.disconnect();
    window.removeEventListener('resize', apply);
    window.visualViewport?.removeEventListener('resize', apply);
    window.removeEventListener('pageshow', apply);
    delete scene.dataset.mapScaleLocked;
    delete scene.__idlemonMapScaleCleanup;
    scene.style.removeProperty('--map-scale');
  };
  scene.__idlemonMapScaleCleanup = cleanup;
  return cleanup;
}
